import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Button,
  Space,
  Typography,
  Alert,
  Checkbox,
  Select,
  Row,
  Col,
  Tag,
} from "antd";
import { MailOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useNotification } from "../../../../hooks/useNotification";
import { useGrades } from "../../../../hooks/useGrades";
import {
  resendBulkTeacherInvitations,
  resendBulkStudentWelcomes,
  getSchoolUsersBySchoolId,
} from "../../../../services/schools/school.service";
import {
  getTeachersBySchool,
  getStudentsBySchool,
} from "../../../../services/users/user.service";
import {
  TeacherEntity,
  StudentEntity,
} from "../../../../services/users/user.types";
import { SchoolUserEntity } from "../../../../services/schools/school.types";

const { Title, Text } = Typography;
const { Option } = Select;

type UserType = "teachers" | "students" | "coordinators";
type UserEntity = TeacherEntity | StudentEntity | SchoolUserEntity;

interface ResendInvitationsModalProps {
  visible: boolean;
  onClose: () => void;
  schoolId: number | null;
}

const ResendInvitationsModal: React.FC<ResendInvitationsModalProps> = ({
  visible,
  onClose,
  schoolId,
}) => {
  const { notify } = useNotification();
  const { grades } = useGrades();
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [isResending, setIsResending] = useState(false);
  const [users, setUsers] = useState<UserEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState<UserType>("teachers");
  const [selectedGrade, setSelectedGrade] = useState<number | undefined>(
    undefined
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Cargar usuarios cuando se abre el modal o cambian los filtros
  const loadUsers = async (page = 1, pageSize = 10, gradeId?: number) => {
    if (!schoolId) return;

    setLoading(true);
    try {
      let response;

      if (userType === "coordinators") {
        // Para coordinadores, usar el servicio de usuarios de escuela con filtro de rol
        const filters: any = { role: "COORDINATOR" };
        // Los coordinadores no tienen grados, así que ignoramos gradeId
        response = await getSchoolUsersBySchoolId(
          schoolId,
          page,
          pageSize,
          filters
        );
      } else {
        // Para profesores y estudiantes, usar los servicios específicos
        response =
          userType === "teachers"
            ? await getTeachersBySchool(schoolId, page, pageSize, gradeId)
            : await getStudentsBySchool(schoolId, page, pageSize, gradeId);
      }

      setUsers(response.data);
      setPagination({
        current: response.page || page,
        pageSize: response.pageSize || pageSize,
        total: response.total,
      });
    } catch (error) {
      console.error("Error loading users:", error);
      const userTypeText =
        userType === "teachers"
          ? "profesores"
          : userType === "coordinators"
          ? "coordinadores"
          : "estudiantes";
      notify("error", "Error", `Error al cargar los ${userTypeText}.`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && schoolId) {
      loadUsers(1, 10, selectedGrade);
      setSelectedUsers([]);
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [visible, schoolId, userType, selectedGrade]);

  const handleSelectUser = (userId: number, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(users.map((user: UserEntity) => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserTypeChange = (value: UserType) => {
    setUserType(value);
    setSelectedUsers([]);
    setSelectedGrade(undefined);
  };

  const handleGradeChange = (value: number | undefined) => {
    setSelectedGrade(value);
    setSelectedUsers([]);
  };

  const handleResendInvitations = async () => {
    if (selectedUsers.length === 0) {
      const userTypeText =
        userType === "teachers"
          ? "profesor"
          : userType === "coordinators"
          ? "coordinador"
          : "estudiante";
      notify(
        "warning",
        "Advertencia",
        `Selecciona al menos un ${userTypeText}.`
      );
      return;
    }

    if (selectedUsers.length > 50) {
      notify(
        "error",
        "Error",
        "No se pueden enviar más de 50 invitaciones a la vez."
      );
      return;
    }

    setIsResending(true);
    try {
      // Usar el endpoint correspondiente según el tipo de usuario
      // Los coordinadores usan el mismo servicio que los profesores
      const result =
        userType === "teachers" || userType === "coordinators"
          ? await resendBulkTeacherInvitations(selectedUsers)
          : await resendBulkStudentWelcomes(selectedUsers);

      if (result?.success) {
        const { sent, failed } = result;

        // Obtener el conteo de usuarios válidos según el tipo
        const validCount =
          userType === "teachers" || userType === "coordinators"
            ? (result as any).valid_teachers
            : (result as any).valid_students;

        if (sent > 0) {
          const messageType =
            userType === "students" ? "mensaje de bienvenida" : "invitación";
          notify(
            "success",
            "Éxito",
            `Se enviaron ${sent} ${messageType}${
              sent !== 1 ? "es" : ""
            } correctamente.`
          );
        }

        if (failed > 0) {
          const messageType =
            userType === "students" ? "mensaje" : "invitación";
          notify(
            "warning",
            "Advertencia",
            `${failed} ${messageType}${failed !== 1 ? "es" : ""} no pudo${
              failed !== 1 ? "n" : ""
            } ser enviada${failed !== 1 ? "s" : ""}.`
          );
        }

        if (validCount && validCount < selectedUsers.length) {
          const invalid = selectedUsers.length - validCount;
          const userTypeText =
            userType === "teachers"
              ? "profesor"
              : userType === "coordinators"
              ? "coordinador"
              : "estudiante";
          notify(
            "info",
            "Información",
            `${invalid} usuario${invalid !== 1 ? "s" : ""} seleccionado${
              invalid !== 1 ? "s" : ""
            } no era${invalid !== 1 ? "n" : ""} ${userTypeText}${
              invalid !== 1 ? "es" : ""
            } válido${invalid !== 1 ? "s" : ""}.`
          );
        }

        // Cerrar modal tras éxito
        onClose();
      } else {
        const errorMessage =
          userType === "students"
            ? "los mensajes de bienvenida"
            : "las invitaciones";
        notify(
          "error",
          "Error",
          result?.message || `Error al enviar ${errorMessage}.`
        );
      }
    } catch (error: any) {
      console.error("Error resending invitations:", error);
      const errorMessage =
        userType === "students"
          ? "los mensajes de bienvenida"
          : "las invitaciones";
      notify("error", "Error", `Ocurrió un error al enviar ${errorMessage}.`);
    } finally {
      setIsResending(false);
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedUsers.length === users.length && users.length > 0}
          indeterminate={
            selectedUsers.length > 0 && selectedUsers.length < users.length
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          Seleccionar todo
        </Checkbox>
      ),
      dataIndex: "select",
      key: "select",
      width: 160,
      render: (_: any, record: UserEntity) => (
        <Checkbox
          checked={selectedUsers.includes(record.id)}
          onChange={(e) => handleSelectUser(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: "Nombre",
      dataIndex: "first_name",
      key: "first_name",
      width: 200,
      render: (firstName: string, record: UserEntity) => (
        <Text strong>
          {firstName} {record.last_name}
        </Text>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
      render: (email: string) => <Text>{email}</Text>,
    },
    {
      title: "Grados",
      dataIndex: "grades",
      key: "grades",
      width: 200,
      render: (userGrades: any, record: UserEntity) => {
        // Manejar coordinadores que pueden venir del SchoolUserEntity
        if ("role" in record && record.role === "COORDINATOR") {
          return (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Rol administrativo
            </Text>
          );
        }

        // Si no hay grados definidos
        if (!userGrades || userGrades.length === 0) {
          return (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Sin grado
            </Text>
          );
        }

        // Manejar diferentes formatos de grados
        return (
          <Space size={4} wrap>
            {Array.isArray(userGrades) ? (
              userGrades.map((grade: any, index: number) => (
                <Tag
                  key={grade.id || index}
                  color="blue"
                  style={{ fontSize: "11px" }}
                >
                  {grade.grade || grade.code || `Grado ${grade.id || grade}`}
                </Tag>
              ))
            ) : (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Sin grado
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            <MailOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Enviar Invitaciones a{" "}
              {userType === "teachers"
                ? "Profesores"
                : userType === "coordinators"
                ? "Coordinadores"
                : "Estudiantes"}
            </Title>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={[
          <Button key="cancel" onClick={onClose} disabled={isResending}>
            Cancelar
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<MailOutlined />}
            onClick={handleResendInvitations}
            loading={isResending}
            disabled={selectedUsers.length === 0}
          >
            {userType === "students"
              ? "Enviar Bienvenidas"
              : "Enviar Invitaciones"}{" "}
            ({selectedUsers.length})
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* Filtros */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Text strong>Tipo de usuario:</Text>
              <Select
                value={userType}
                onChange={handleUserTypeChange}
                style={{ width: "100%", marginTop: 4 }}
              >
                <Option value="teachers">Profesores</Option>
                <Option value="coordinators">Coordinadores</Option>
                <Option value="students">Estudiantes</Option>
              </Select>
            </Col>
            <Col span={12}>
              <Text strong>Filtrar por grado:</Text>
              <Select
                value={selectedGrade}
                onChange={handleGradeChange}
                style={{ width: "100%", marginTop: 4 }}
                placeholder={
                  userType === "coordinators"
                    ? "Los coordinadores no tienen grados"
                    : "Todos los grados"
                }
                allowClear
                disabled={userType === "coordinators"}
              >
                {grades.map((grade) => (
                  <Option key={grade.id} value={grade.id}>
                    {grade.grade}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Información del modal */}
          <Alert
            message={
              userType === "students"
                ? "Reenvío de Mensajes de Bienvenida"
                : "Reenvío de Invitaciones"
            }
            description={
              userType === "students"
                ? "Esta herramienta reenvía los mensajes de bienvenida a los estudiantes seleccionados. Útil cuando un estudiante no ha recibido su mensaje inicial o cuando se ha actualizado su información. Se pueden enviar hasta 50 mensajes por vez."
                : userType === "coordinators"
                ? "Esta herramienta debe usarse únicamente para reenviar el email de bienvenida (donde se crea la contraseña) en caso de que se haya editado el email del coordinador o si nunca lo recibió. Se pueden enviar hasta 50 invitaciones por vez."
                : "Esta herramienta debe usarse únicamente para reenviar el email de bienvenida (donde se crea la contraseña) en caso de que se haya editado el email del profesor o si nunca lo recibió. Se pueden enviar hasta 50 invitaciones por vez."
            }
            type="warning"
            icon={<ExclamationCircleOutlined />}
            showIcon
          />

          {/* Estadísticas */}
          <div style={{ marginBottom: 16 }}>
            <Text>
              <strong>
                Total de{" "}
                {userType === "teachers"
                  ? "profesores"
                  : userType === "coordinators"
                  ? "coordinadores"
                  : "estudiantes"}
                :
              </strong>{" "}
              {users.length} |<strong> Seleccionados:</strong>{" "}
              {selectedUsers.length} |<strong> Límite:</strong> 50
            </Text>
          </div>

          {/* Tabla de usuarios */}
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: false,
              showTotal: (total, range) => {
                const userTypeText =
                  userType === "teachers"
                    ? "profesores"
                    : userType === "coordinators"
                    ? "coordinadores"
                    : "estudiantes";
                return `${range[0]}-${range[1]} de ${total} ${userTypeText}`;
              },
              onChange: (page, pageSize) => {
                loadUsers(page, pageSize, selectedGrade);
              },
            }}
            scroll={{ y: 300 }}
            locale={{
              emptyText: (() => {
                const userTypeText =
                  userType === "teachers"
                    ? "profesores"
                    : userType === "coordinators"
                    ? "coordinadores"
                    : "estudiantes";
                return `No hay ${userTypeText} disponibles para enviar invitaciones.`;
              })(),
            }}
          />

          {/* Advertencia sobre el límite */}
          {selectedUsers.length > 50 && (
            <Alert
              message="Límite excedido"
              description={(() => {
                const userTypeText =
                  userType === "teachers"
                    ? "profesores"
                    : userType === "coordinators"
                    ? "coordinadores"
                    : "estudiantes";
                return `Has seleccionado más de 50 ${userTypeText}. Por favor, reduce la selección a máximo 50.`;
              })()}
              type="error"
              showIcon
            />
          )}
        </Space>
      </Modal>
    </>
  );
};

export default ResendInvitationsModal;
